import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import download from "downloadjs";
import { toast } from "react-toastify";
import API from "../../api/axios";
import { APP_URLS } from "../../api/url";
import NoRecord from "../../assets/SharedComponents/Table/NoRecord";
import {
  APIError,
  checkForSuperAdmin,
  downloadFileError,
  getActiveLanguages,
} from "../../core/utils";
import {
  TRANSLATION_FILE_TABS,
  TRANSLATION_FILE_TYPE,
  TRANSLATION_UPLOAD_LANGUAGES,
} from "../../config/constant";
import { TranslationFileManagementWrapper } from "./style/translationFileManagement";

const getTypeFromSearchParams = (searchParams) => {
  const type = Number(searchParams.get("type"));
  if (type === TRANSLATION_FILE_TYPE.WEB) {
    return TRANSLATION_FILE_TYPE.WEB;
  }
  return TRANSLATION_FILE_TYPE.MOBILE;
};

const parseTranslationsList = (data) => {
  if (!data) return [];
  if (Array.isArray(data.translations)) return data.translations;
  if (Array.isArray(data.items)) return data.items;
  return [];
};

const TranslationFileManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [translationType, setTranslationType] = useState(() =>
    getTypeFromSearchParams(searchParams)
  );

  const activeLanguages = getActiveLanguages();
  const languages = Array.isArray(activeLanguages) ? activeLanguages : [];

  const getLanguageCode = (item) => item.key ?? item.language_code;

  const getLanguageLabel = (code) => {
    const fromUploadList = TRANSLATION_UPLOAD_LANGUAGES.find(
      (l) => l.code === code
    )?.name;
    if (fromUploadList) return fromUploadList;
    return languages.find((l) => l.code === code)?.name ?? code?.toUpperCase();
  };

  const getTranslationDownloadUrl = (id) =>
    `${APP_URLS.TRANSLATIONS_LIST}/${id}/download/${translationType}`;

  useEffect(() => {
    setTranslationType(getTypeFromSearchParams(searchParams));
  }, [searchParams]);

  const fetchTranslations = useCallback(() => {
    setLoading(true);
    API.get(`${APP_URLS.TRANSLATIONS_LIST}/${translationType}`)
      .then((res) => {
        const resp = res.data;
        if (resp.success === true) {
          setTranslations(parseTranslationsList(resp.data));
        } else {
          toast.error(resp.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(APIError(error), {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  }, [translationType]);

  useEffect(() => {
    if (!checkForSuperAdmin()) {
      navigate("/dashboard", { replace: true });
      return;
    }
    fetchTranslations();
  }, [navigate, fetchTranslations]);

  const handleDownload = (item) => {
    if (!item?.id || downloadingKey) return;

    const languageCode = getLanguageCode(item);
    setDownloadingKey(item.id);
    API.get(getTranslationDownloadUrl(item.id), { responseType: "blob" })
      .then((res) => {
        const resp = res.data;
        if (resp !== null) {
          toast.success("Downloading. Please wait", {
            position: toast.POSITION.TOP_RIGHT,
          });
          const content = res.headers["content-type"];
          const typeLabel =
            translationType === TRANSLATION_FILE_TYPE.MOBILE ? "mobile" : "web";
          const filename = `translations-${typeLabel}-${languageCode || item.id}.json`;
          download(resp, filename, content);
        } else {
          toast.error("Download failed", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        setDownloadingKey(null);
      })
      .catch(async (error) => {
        setDownloadingKey(null);
        const error_message = await downloadFileError(error);
        toast.error(error_message, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  return (
    <TranslationFileManagementWrapper>
      <div className="auctionMainContainer">
        <div className="row mt-4">
          <div className="col-md-4 offset-md-4">
            <h1>Translation File Management</h1>
          </div>
        </div>
        <div className="translation-toolbar">
          <NavLink
            className="upload-btn"
            to={`/upload-translations/add?type=${translationType}`}
          >
            + Upload
          </NavLink>
          <div
            className="translation-toolbar__type"
            role="tablist"
            aria-label="Translation platform"
          >
            <span className="translation-toolbar__label">Platform</span>
            <div className="translation-type-tabs">
              {TRANSLATION_FILE_TABS.map((tab) => (
                <button
                  key={tab.type}
                  type="button"
                  role="tab"
                  aria-selected={translationType === tab.type}
                  className={`type-tab ${
                    translationType === tab.type ? "type-tab--active" : ""
                  }`}
                  onClick={() => {
                    setTranslationType(tab.type);
                    setSearchParams({ type: String(tab.type) });
                  }}
                >
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="tableContainer">
          <table className="auctionTable">
            <thead className="tableHead">
              <tr className="table-row">
                <th>ID</th>
                <th>Language</th>
                <th>Download</th>
              </tr>
            </thead>
            {loading ? (
              <div className="d-flex justify-content-center three-dot">
                <ThreeDots
                  height="150"
                  width="150"
                  radius="9"
                  color="#637df4"
                  ariaLabel="three-dots-loading"
                  visible={true}
                />
              </div>
            ) : (
              <tbody>
                {translations.length > 0 ? (
                  translations.map((item) => {
                    const languageCode = getLanguageCode(item);
                    return (
                      <tr key={item.id ?? languageCode} className="table-row">
                        <td>{item.id}</td>
                        <td>{getLanguageLabel(languageCode)}</td>
                        <td className="action">
                          <button
                            type="button"
                            className="accept"
                            onClick={() => handleDownload(item)}
                            disabled={downloadingKey !== null || !item.id}
                          >
                            {downloadingKey === item.id
                              ? "Downloading..."
                              : "Download"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <NoRecord />
                )}
              </tbody>
            )}
          </table>
        </div>
      </div>
    </TranslationFileManagementWrapper>
  );
};

export default TranslationFileManagement;
